import { useEffect, useState } from 'react'
import { Landmark, UserPen, Loader2, ShieldAlert, Check } from 'lucide-react'
import api from '../../services/api'

/**
 * Doing something FOR a consultant who cannot do it themselves.
 *
 * A consultant sent their bank details to the admin because they could not enter them, and the
 * admin had nowhere to put them. That is the gap this closes.
 *
 * The two rules this screen exists to enforce, both visible rather than buried in the backend:
 *
 *  1. Who may READ an account number is narrower than who may WRITE one. The Admin, the Master
 *     PM and a Super Admin see it in full and it is prefilled, because they are the people who
 *     chase a failed payment or check an account against what the consultant sent — and asking
 *     the consultant to resend it by email instead would put the number somewhere far worse
 *     than behind a login. Anyone else holding act_on_behalf can still enter and correct
 *     details, but gets only the last four digits back. Every read is recorded either way.
 *
 *  2. Nothing here is quiet. Saving bank details emails the consultant, and the screen says so
 *     before the button is pressed, not after. An admin who knows the consultant will be told is
 *     an admin doing this for the right reasons.
 */
export default function ActForConsultant({ userId, userName, person, onDone }) {
  const [tab, setTab] = useState(null)          // 'banking' | 'profile' | null
  const [banking, setBanking] = useState(null)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)          // {kind, text}
  const [form, setForm] = useState({
    account_holder_name: '', mailing_address: '', bank_name: '', bank_address: '',
    account_number: '', iban: '', swift_code: '', currency: 'USD', country: '',
  })
  // Prefilled from the record, not empty. An admin correcting an address needs to see what is
  // on file — otherwise a one-character fix means retyping the whole field, and they cannot tell
  // a blank "not set" from a blank "I have not looked".
  const [profile, setProfile] = useState({
    full_name: person?.full_name || '',
    phone_number: person?.phone_number || '',
    specialization: person?.specialization || '',
    linkedin_url: person?.linkedin_url || '',
    company_name: person?.company_name || '',
    about: person?.about || '',
  })
  // Re-sync if the panel is reopened on somebody else without unmounting.
  useEffect(() => {
    setProfile({
      full_name: person?.full_name || '',
      phone_number: person?.phone_number || '',
      specialization: person?.specialization || '',
      linkedin_url: person?.linkedin_url || '',
      company_name: person?.company_name || '',
      about: person?.about || '',
    })
  }, [person?.id])          // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (tab !== 'banking' || banking) return
    api.get(`/admin/people/${userId}/banking`)
      .then(({ data }) => {
        setBanking(data)
        // The Admin, Master PM and Super Admin get the real number back and it is prefilled,
        // so correcting one wrong digit is a one-character edit. Anyone else who may edit but
        // not read gets nothing in those two boxes — a prefilled mask would be a value they
        // could save back as if it were the account.
        if (data.has_banking) {
          setForm(f => ({
            ...f,
            account_holder_name: data.account_holder_name || '',
            mailing_address: data.mailing_address || '',
            bank_name: data.bank_name || '',
            bank_address: data.bank_address || '',
            swift_code: data.swift_code || '',
            currency: data.currency || 'USD',
            country: data.country || '',
            account_number: data.can_see_full ? (data.account_number || '') : '',
            iban: data.can_see_full ? (data.iban || '') : '',
          }))
        }
      })
      .catch(e => setMsg({ kind: 'error', text: e.response?.data?.detail || 'Could not load bank details.' }))
  }, [tab, userId, banking])

  const saveBanking = async () => {
    setSaving(true); setMsg(null)
    try {
      const { data } = await api.put(`/admin/people/${userId}/banking`, form)
      setMsg({ kind: 'ok', text: data.detail })
      setBanking(null)                                    // re-read, so the masked view refreshes
      setForm(f => ({ ...f, account_number: '', iban: '' }))
      onDone?.()
    } catch (e) {
      // Validation errors from the shared payload come back as a readable string.
      const d = e.response?.data?.detail
      setMsg({ kind: 'error', text: Array.isArray(d) ? d.map(x => x.msg).join('; ') : (d || 'Could not save.') })
    } finally { setSaving(false) }
  }

  const saveProfile = async () => {
    setSaving(true); setMsg(null)
    // Only what DIFFERS from the record. Now that the form arrives prefilled, sending every
     // non-empty field would rewrite values the admin never touched — harmless in effect, but it
     // would put them in the audit log as changes the admin did not make.
    const body = Object.fromEntries(
      Object.entries(profile).filter(([k, v]) => (v ?? '') !== (person?.[k] ?? ''))
    )
    if (!Object.keys(body).length) { setMsg({ kind: 'error', text: 'Nothing has changed.' }); setSaving(false); return }
    try {
      const { data } = await api.put(`/admin/people/${userId}/profile`, body)
      setMsg({ kind: 'ok', text: data.detail })
      onDone?.()
    } catch (e) {
      const d = e.response?.data?.detail
      setMsg({ kind: 'error', text: Array.isArray(d) ? d.map(x => x.msg).join('; ') : (d || 'Could not save.') })
    } finally { setSaving(false) }
  }

  const field = (label, key, obj, setObj, opts = {}) => (
    <label className="block">
      <span className="block text-xs font-medium text-gray-600 mb-1">
        {label}{opts.required && <span className="text-red-500"> *</span>}
      </span>
      {opts.rows ? (
        <textarea
          rows={opts.rows} value={obj[key] || ''}
          onChange={e => setObj(o => ({ ...o, [key]: e.target.value }))}
          placeholder={opts.placeholder || ''}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        />
      ) : (
        <input
          type="text" value={obj[key] || ''}
          onChange={e => setObj(o => ({ ...o, [key]: e.target.value }))}
          placeholder={opts.placeholder || ''}
          className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
        />
      )}
      {opts.hint && <span className="block text-[11px] text-gray-400 mt-0.5">{opts.hint}</span>}
    </label>
  )

  return (
    <div className="border border-gray-200 rounded-xl">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
        <UserPen className="w-4 h-4 text-indigo-600" />
        <h4 className="text-sm font-semibold text-gray-800 flex-1">Do this for {userName}</h4>
        <button
          type="button"
          onClick={() => { setTab(tab === 'banking' ? null : 'banking'); setMsg(null) }}
          className={`text-xs px-2.5 py-1 rounded-lg border ${tab === 'banking'
            ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
            : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
        >
          Bank details
        </button>
        <button
          type="button"
          onClick={() => { setTab(tab === 'profile' ? null : 'profile'); setMsg(null) }}
          className={`text-xs px-2.5 py-1 rounded-lg border ${tab === 'profile'
            ? 'bg-indigo-50 border-indigo-200 text-indigo-700'
            : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
        >
          Profile &amp; address
        </button>
      </div>

      {!tab && (
        <p className="px-4 py-3 text-xs text-gray-500">
          For a consultant who cannot fill something in themselves. Everything you change here is
          recorded against your name, and bank details are emailed to the consultant.
        </p>
      )}

      {msg && (
        <div className={`mx-4 mt-3 rounded-lg px-3 py-2 text-xs flex items-start gap-2 ${
          msg.kind === 'error' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
          {msg.kind === 'ok' && <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}

      {tab === 'banking' && (
        <div className="p-4 space-y-3">
          {/* Said before the button is pressed, not after. */}
          <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
            <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-900">
              This is the account {userName} gets paid into. Saving emails them the bank name and
              the last four digits so they can confirm it was expected, and marks the details
              unverified until they do. Opening this panel is recorded against your name.
            </p>
          </div>

          {!banking ? (
            <p className="text-xs text-gray-500 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading what is on file…
            </p>
          ) : banking.has_banking ? (
            <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 flex items-center gap-2 flex-wrap">
              <Landmark className="w-3.5 h-3.5" />
              <span>
                On file: <strong>{banking.bank_name}</strong>, account{' '}
                <span className="font-mono">
                  {banking.can_see_full ? (banking.account_number || banking.account_number_masked)
                                        : banking.account_number_masked}
                </span>
                {banking.can_see_full && banking.iban && <> · IBAN <span className="font-mono">{banking.iban}</span></>}
              </span>
              {banking.last_updated_by && <span>· last set by {banking.last_updated_by}</span>}
              {banking.verified
                ? <span className="text-green-700">· confirmed by the consultant</span>
                : <span className="text-amber-700">· not yet confirmed</span>}
            </div>
          ) : (
            <p className="text-xs text-gray-500">Nothing on file yet.</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field('Account holder name', 'account_holder_name', form, setForm, { required: true })}
            {field('Bank name', 'bank_name', form, setForm, { required: true })}
            {field('Account number', 'account_number', form, setForm, {
              required: true,
              hint: banking?.has_banking && !banking?.can_see_full
                ? 'Type the full number again — your role does not display the stored one'
                : undefined,
            })}
            {field('IBAN', 'iban', form, setForm, { hint: 'Optional — many accounts use only the account number' })}
            {field('SWIFT / BIC', 'swift_code', form, setForm, { required: true })}
            {field('Country', 'country', form, setForm, { required: true })}
            {field('Currency', 'currency', form, setForm, { required: true, placeholder: 'USD' })}
          </div>
          {field('Bank address', 'bank_address', form, setForm, { required: true, rows: 2 })}
          {field("Consultant's mailing address", 'mailing_address', form, setForm, { required: true, rows: 2 })}

          <button
            type="button" onClick={saveBanking} disabled={saving}
            className="px-3.5 py-2 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-40"
          >
            {saving ? 'Saving…' : `Save and email ${userName}`}
          </button>
        </div>
      )}

      {tab === 'profile' && (
        <div className="p-4 space-y-3">
          <p className="text-xs text-gray-500">
            Already filled in with what is on file — edit what needs changing. Only fields you
            actually change are saved. Email address and role are not here: those are account
            changes, not profile details.
            {' '}The <strong>mailing address</strong> lives under <strong>Bank details</strong>,
            because that is the address the consultancy agreement is issued to.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {field('Full name', 'full_name', profile, setProfile)}
            {field('Phone number', 'phone_number', profile, setProfile)}
            {field('Specialisation', 'specialization', profile, setProfile)}
            {field('LinkedIn URL', 'linkedin_url', profile, setProfile)}
            {field('Company / organisation', 'company_name', profile, setProfile)}
          </div>
          {field('About', 'about', profile, setProfile, { rows: 3 })}
          <button
            type="button" onClick={saveProfile} disabled={saving}
            className="px-3.5 py-2 rounded-lg bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      )}
    </div>
  )
}
